/**
 * Rules Manager — bulk popup invoked from the Ads Manager "Rules"
 * dropdown when ≥1 entity is selected. Available on all 4 grains:
 * Campaigns / Ad Groups / Keywords / Search Terms.
 *
 * Two actions, scoped to the selected entities:
 *   - Apply rules → attach the picked rules to the selected entities.
 *                   The rule then evaluates them per its own definition
 *                   (engine descends from the attachment).
 *   - Stop rules  → stop the picked rules for the selected entities.
 *                   Works even when the rule is attached at an ancestor
 *                   level (app / campaign) — the backend records an
 *                   exclusion the evaluator subtracts.
 *
 * "Create New Rule" deep-links to the Rules section with the create
 * modal open (?create=1).
 *
 * Level compatibility: a rule shows on a grain iff its own level is the
 * SAME or DEEPER (campaign → ad_group → keyword), so the attachment
 * descends to the rule's level (a keyword rule applied on a campaign
 * fires on every keyword under it). A shallower rule (campaign rule on
 * the Ad Groups tab) is hidden. Search Term is a separate axis — only
 * search-term rules on the Search Terms tab, and vice versa.
 *
 * Max-Conversions guard (campaign grain only): rules that can ONLY act
 * on Max-Conversions campaigns (campaign-level Go-to-Goal) are hidden
 * when none of the selected campaigns is Max-Conv. When some are, the
 * rule is shown; on Apply the backend applies it only to the Max-Conv
 * campaigns and reports the rest — surfaced in the result toast.
 */

import { useEffect, useMemo, useState } from "react";
import { useDiscardGuard } from "@/lib/discard-guard";
import { levelCompatible, ruleFitsApp } from "@/lib/rules-manager-filter";

import {
  useCreateRulesBulkJob,
  useRulesV2,
  type CircleConfigV2,
  type RuleActionV2,
  type RuleConditionV2,
  type RuleV2,
} from "@/lib/api-hooks";
import { notice, trackJob } from "@/lib/rules-bulk-jobs";

type Mode = "apply" | "stop";

export type RulesManagerEntityType =
  | "campaign"
  | "ad_group"
  | "keyword"
  | "search_term";

export type SearchTermRef = {
  campaign_id: number;
  ad_group_id: number;
  text: string;
};

const GOAL_OBJECTS = new Set([
  "Go to: Goal 1",
  "Go to: Goal 2",
  "Go to: Goal 3",
]);

function circle1(r: RuleV2): CircleConfigV2 {
  const c = r.circle_1_config as Partial<CircleConfigV2> | undefined;
  return {
    conditions: Array.isArray(c?.conditions) ? c!.conditions : [],
    actions: Array.isArray(c?.actions) ? c!.actions : [],
    schedule: c?.schedule ?? null,
  };
}

/** A rule that can only act on Max-Conversions campaigns: campaign-level
 *  rule whose action is a Go-to-Goal (mutates targetCpa). */
function ruleRequiresMaxConv(r: RuleV2): boolean {
  if (r.entity_level !== "campaign") return false;
  return circle1(r).actions.some((a) => GOAL_OBJECTS.has(String(a.object)));
}

function condText(cond: RuleConditionV2 | undefined): string {
  if (!cond) return "—";
  const val =
    cond.val2 != null && cond.val2 !== ""
      ? `${cond.val ?? ""}…${cond.val2}`
      : `${cond.val ?? ""}`;
  return `${cond.metric} ${cond.op} ${val}`.trim();
}

function actionText(act: RuleActionV2 | undefined): string {
  if (!act) return "—";
  const parts = [act.object];
  if (act.action) parts.push(String(act.action));
  if (act.val != null && act.val !== "") parts.push(String(act.val));
  return parts.join(" ");
}

function actionTint(act: RuleActionV2 | undefined): string {
  const s = actionText(act);
  if (/pause/i.test(s)) return "var(--c-text-3)";
  if (/\+|increase/i.test(s)) return "var(--c-pos-600)";
  if (/-|decrease/i.test(s)) return "var(--c-neg-600)";
  return "var(--c-accent-600)";
}

function extractMsg(err: unknown): string | null {
  return (
    (err as { response?: { data?: { error?: { message?: string } } } })
      ?.response?.data?.error?.message ?? null
  );
}

const Ico = {
  search: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
  ),
  play: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><polygon points="6 4 20 12 6 20 6 4" /></svg>
  ),
  pause: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="6" y="5" width="4" height="14" rx="1" /><rect x="14" y="5" width="4" height="14" rx="1" /></svg>
  ),
  x: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18" /><path d="m6 6 12 12" /></svg>
  ),
  plus: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14" /><path d="M5 12h14" /></svg>
  ),
  warn: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" /><path d="M12 9v4" /><path d="M12 17h.01" /></svg>
  ),
};

const MODES: { id: Mode; label: string; icon: React.ReactNode }[] = [
  { id: "apply", label: "Apply rules", icon: Ico.play },
  { id: "stop", label: "Stop rules", icon: Ico.pause },
];

export function RulesManagerModal({
  open,
  onClose: onCloseProp,
  entityType,
  entityIds = [],
  searchTerms = [],
  hasMaxConv,
  appId,
  onCreateNew,
}: {
  open: boolean;
  onClose: () => void;
  entityType: RulesManagerEntityType;
  /** campaign / ad_group / keyword ids (ignored for search_term). */
  entityIds?: number[];
  /** search_term refs (ignored for the other grains). */
  searchTerms?: SearchTermRef[];
  /** Campaign grain only: is ≥1 selected campaign Max-Conversions?
   *  Drives hiding of max-conv-only rules. undefined on other grains. */
  hasMaxConv?: boolean;
  /** Приложение выбранных сущностей.
   *
   *  ★ Правило принадлежит ОДНОМУ приложению (§23.1), и привязка к
   *  чужому отвергается сервером. Показывать такое правило значит
   *  предлагать действие, которое заведомо откажет. `null` —
   *  приложение неизвестно (вид «все приложения», разнородное
   *  выделение): тогда не фильтруем, заслон на сервере остаётся. */
  appId?: number | null;
  onCreateNew: () => void;
}) {
  const [mode, setMode] = useState<Mode>("apply");
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const rulesQuery = useRulesV2();
  const createJob = useCreateRulesBulkJob();

  // Reset transient state each open (component stays mounted).
  useEffect(() => {
    if (open) {
      setSelected(new Set());
      setQuery("");
      setMode("apply");
      setError(null);
    }
  }, [open]);

  const guard = useDiscardGuard(open, selected.size > 0, onCloseProp);
  const onClose = guard.requestClose;

  const selectionCount =
    entityType === "search_term"
      ? new Set(searchTerms.map((t) => t.text)).size
      : entityIds.length;

  const rules = useMemo(() => {
    // Level compatibility first: only rules at this grain or deeper.
    let rows = (rulesQuery.data ?? []).filter((r) =>
      levelCompatible(entityType, r.entity_level),
    );
    // Правило без приложения ещё ни к чему не привязано — оно подойдёт
    // любому; привязанное к другому приложению сервер отвергнет.
    if (appId != null) {
      rows = rows.filter((r) => ruleFitsApp(r.app_id, appId));
    }
    // Then, on the campaign grain, hide max-conv-only rules when no
    // selected campaign is Max-Conversions (they couldn't apply).
    if (entityType === "campaign" && hasMaxConv === false) {
      rows = rows.filter((r) => !ruleRequiresMaxConv(r));
    }
    return rows;
  }, [rulesQuery.data, entityType, hasMaxConv, appId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return q ? rules.filter((r) => r.name.toLowerCase().includes(q)) : rules;
  }, [rules, query]);

  if (!open) return null;

  const stop = mode === "stop";
  const toggle = (id: number) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  };
  const allChecked = filtered.length > 0 && filtered.every((r) => selected.has(r.id));
  const someChecked = filtered.some((r) => selected.has(r.id));
  const toggleAll = () =>
    setSelected(allChecked ? new Set() : new Set(filtered.map((r) => r.id)));

  const canApply = selected.size > 0 && selectionCount > 0 && !busy;
  const nounEnt = `${selectionCount} ${entityType.replace("_", " ")}${
    selectionCount === 1 ? "" : "s"
  }`;
  const applyLabel = stop
    ? selected.size
      ? `Stop ${selected.size}`
      : "Stop"
    : selected.size
    ? `Apply ${selected.size}`
    : "Apply";

  // ★ Apply / Stop идут фоновым заданием: здесь только его заводим.
  // Ответ — номер задания, модалка закрывается сразу, итог приходит
  // тостером (RulesBulkJobsToaster). Пересборка покрытия после ECG
  // шла больше минуты, и всё это время модалка висела в «Working…».
  // Упал сам запрос (права, нет своих сущностей) — модалка остаётся,
  // ошибка — в ней.
  const run = async () => {
    if (!canApply) return;
    setBusy(true);
    setError(null);
    try {
      const resp = await createJob.mutateAsync({
        action: stop ? "stop" : "apply",
        entity_type: entityType,
        rule_ids: Array.from(selected),
        ...(entityType === "search_term"
          ? { search_terms: searchTerms }
          : { entity_ids: entityIds }),
      });
      onCloseProp();
      if (resp.already_in_progress) notice("Already in progress");
      trackJob(resp.job);
    } catch (err) {
      setError(extractMsg(err) ?? `Couldn't ${stop ? "stop" : "apply"} rules`);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-shell rm-shell"
        role="dialog"
        aria-modal="true"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-head">
          <div>
            <div className="modal-title">
              Rules <em>Manager</em>
            </div>
            <div className="modal-sub">
              Choose an action, then pick which rules it applies to —
              scoped to your {nounEnt}.
            </div>
          </div>
          <button className="modal-x" aria-label="Close" onClick={onClose}>
            {Ico.x}
          </button>
        </div>

        <div className="modal-body rm-body">
          {/* Left rail */}
          <div className="rm-rail">
            <div className="rm-rail-label">Action</div>
            {MODES.map((m) => (
              <button
                key={m.id}
                type="button"
                className={
                  "rm-mode-card" +
                  (mode === m.id ? " is-active" : "") +
                  (m.id === "stop" ? " is-stop" : "")
                }
                onClick={() => setMode(m.id)}
              >
                <span className="rm-mode-ico">{m.icon}</span>
                <span style={{ minWidth: 0 }}>
                  <span className="rm-mode-name">{m.label}</span>
                </span>
              </button>
            ))}
            <div className="rm-rail-divider" />
            <button
              type="button"
              className="btn primary rm-create-btn"
              onClick={onCreateNew}
            >
              {Ico.plus}
              Create New Rule
            </button>
          </div>

          {/* Right: rules list */}
          <div className="rm-right">
            <div className="rm-search">
              <div className="rm-search-wrap">
                <span className="rm-search-ico">{Ico.search}</span>
                <input
                  className="field-input"
                  placeholder="Search rules by name"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </div>
            </div>
            <div className="rm-list-head">
              <label className="rm-check">
                <input
                  type="checkbox"
                  checked={allChecked}
                  ref={(el) => {
                    if (el) el.indeterminate = !allChecked && someChecked;
                  }}
                  onChange={toggleAll}
                />
                <span>Select all</span>
              </label>
              <span className="rm-count">
                {filtered.length} rule{filtered.length === 1 ? "" : "s"}
              </span>
            </div>
            <div className="rm-list">
              {rulesQuery.isLoading ? (
                <div className="rm-empty">Loading rules…</div>
              ) : filtered.length === 0 ? (
                <div className="rm-empty">
                  <div style={{ fontSize: 14, fontWeight: 500, color: "var(--c-text-2)" }}>
                    {query ? `No rules match “${query}”` : "No rules yet"}
                  </div>
                  <div style={{ fontSize: 12.5, marginTop: 6 }}>
                    {query ? "Try a different search." : "Create one to get started."}
                  </div>
                </div>
              ) : (
                filtered.map((r) => {
                  const c1 = circle1(r);
                  const checked = selected.has(r.id);
                  return (
                    <div
                      key={r.id}
                      className={"rm-rule-row" + (checked ? " is-sel" : "")}
                      onClick={() => toggle(r.id)}
                    >
                      <input type="checkbox" checked={checked} readOnly />
                      <div className="rm-rule-main">
                        <div className="rm-rule-name">{r.name}</div>
                        <div className="rm-rule-meta">
                          <span
                            className="badge"
                            style={{ fontSize: 10, padding: "1px 7px", height: 17 }}
                          >
                            {r.entity_level}
                          </span>
                          <span className="rm-cond">{condText(c1.conditions[0])}</span>
                        </div>
                      </div>
                      <span
                        style={{
                          fontSize: 12,
                          fontWeight: 500,
                          color: actionTint(c1.actions[0]),
                          flex: "none",
                        }}
                      >
                        {actionText(c1.actions[0])}
                      </span>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: r.enabled ? "var(--c-pos-600)" : "var(--c-text-4)",
                          flex: "none",
                          width: 54,
                          textAlign: "right",
                        }}
                      >
                        {r.enabled ? "Active" : "Paused"}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-foot rm-foot">
          <span className="modal-foot-note">
            {error ? (
              <span role="alert" style={{ color: "var(--c-neg-600)" }}>
                {error}
              </span>
            ) : selected.size > 0 ? (
              <span>
                <b style={{ color: "var(--c-text)" }}>{selected.size}</b> rule
                {selected.size === 1 ? "" : "s"} selected ·{" "}
                {stop ? "will be stopped" : "will be applied"}
              </span>
            ) : (
              "Select one or more rules to continue"
            )}
          </span>
          <div className="modal-foot-actions">
            <button type="button" className="btn" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className={"btn primary" + (stop ? " rm-stop-btn" : "")}
              disabled={!canApply}
              onClick={() => void run()}
            >
              {applyLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
